import React from 'react';
import PropTypes from 'prop-types';

import UCD603Logo from '../../../../images/UCD603Logo.png';
import { formatDateTime, reverseFormatDate } from '../../../../utils/dateFunctions';

import local from './ucd603Form.module.css';

const UCD603FormTemplate = ({ surveyAnswers }) => {
    return (
        <div className={local.templateWrapper}>
            <div className={local.logo}>
                <img src={UCD603Logo} alt="UCD603 Form Logo" />
            </div>

            <div className={local.disclaimer}>
                Please note that this form may be shown to the participant
            </div>

            <div className={local.formPage1}>
                <div className={local.formHeading}>
                    UC603 - Failure to participate in the Restart Scheme
                </div>

                <div className={local.divider} />

                <div className={local.heading}>Part 1 - Participant details</div>

                <div className={local.row}>
                    <span className={local.label}>Name</span>
                    <span className={local.answer}>{surveyAnswers.Q_1}</span>
                </div>
                <div className={local.row}>
                    <span className={local.label}>National Insurance number</span>
                    <span className={local.answer}>{surveyAnswers.Q_2}</span>
                </div>

                <div className={local.divider} />

                <div className={local.heading}>
                    Part 2 - Mandatory Activity Notification (MAN) details
                </div>

                <div className={local.row}>
                    <span className={local.number}> 1 </span>
                    <span className={local.numberLabel}>
                        What did you mandate the participant to do?
                    </span>
                    <span className={local.answerBlock}>
                        <span className={local.answerRow}>
                            <span className={local.emptyCheckBox}>
                                {surveyAnswers.Q_3 === 'Item 1' ? 'X' : ''}
                            </span>
                            <span>attend and take part in an appointment</span>
                        </span>
                        <span className={local.answerRow}>
                            <span className={local.emptyCheckBox}>
                                {surveyAnswers.Q_3 === 'Item 2' ? 'X' : ''}
                            </span>
                            <span>undertake work related activity</span>
                        </span>
                    </span>
                </div>

                <div className={local.row}>
                    <span className={local.number}> 2 </span>
                    <span className={local.numberLabel}>
                        If participant was mandated to undertake work related activity, provide
                        details of that activity
                    </span>
                    <span className={local.answer}>{surveyAnswers.Q_4}</span>
                </div>

                <div className={local.row}>
                    <span className={local.number}> 3 </span>
                    <span className={local.numberLabel}>Date MAN issued to the participant</span>
                    <span className={local.answer}>{reverseFormatDate(surveyAnswers.Q_5)}</span>
                </div>

                <div className={local.row}>
                    <span className={local.number}> 4 </span>
                    <span className={local.numberLabelDoubleRow}>How was the MAN issued</span>
                    <span className={local.answerInline}>
                        <span className={local.answerRow}>
                            <span className={local.emptyCheckBox}>
                                {surveyAnswers.Q_6 === 'Item 1' ? 'X' : ''}
                            </span>
                            <span>Post</span>
                        </span>
                        <span className={local.answerRow}>
                            <span className={local.emptyCheckBox}>
                                {surveyAnswers.Q_6 === 'Item 2' ? 'X' : ''}
                            </span>
                            <span>in person</span>
                        </span>
                    </span>
                </div>

                <div className={local.row}>
                    <span className={local.number}> 5 </span>
                    <span className={local.numberLabelAddress}>
                        Provide additional details of mandation: e.g. full address used
                    </span>
                    <span className={local.addressAnswer}>
                        {surveyAnswers.Q_7
                            ? surveyAnswers.Q_7.split('\n').map((el) => <span key={el}>{el}</span>)
                            : ''}
                    </span>
                </div>

                <div className={local.row}>
                    <span className={local.number}> 6 </span>
                    <span className={local.numberLabel}>
                        Date and time of the mandatory appointment or date the activity should have
                        been completed by
                    </span>
                    <span className={local.answer}>{formatDateTime(surveyAnswers.Q_8)}</span>
                </div>

                <div className={local.row}>
                    <span className={local.number}> 7 </span>
                    <span className={local.numberLabelLong}>
                        Does the participant have any known vulnerabilities / complex needs?
                    </span>
                    <span className={local.answerBlock}>
                        <span className={local.answerRow}>
                            <span className={local.emptyCheckBox}>
                                {surveyAnswers.Q_9 === true ? 'X' : ''}
                            </span>
                            <span>Yes</span>
                        </span>
                        <span className={local.answerRow}>
                            <span className={local.emptyCheckBox}>
                                {surveyAnswers.Q_9 === false ? 'X' : ''}
                            </span>
                            <span>No</span>
                        </span>
                    </span>
                </div>

                <div className={local.row}>
                    <span className={local.number}> 8 </span>
                    <span className={local.numberLabelLong}>
                        Was the required appointment/activity reasonable, taking into account the
                        participants circumstances?
                    </span>
                    <span className={local.answerBlock}>
                        <span className={local.answerRow}>
                            <span className={local.emptyCheckBox}>
                                {surveyAnswers.Q_10 === true ? 'X' : ''}
                            </span>
                            <span>Yes</span>
                        </span>
                        <span className={local.answerRow}>
                            <span className={local.emptyCheckBox}>
                                {surveyAnswers.Q_10 === false ? 'X' : ''}
                            </span>
                            <span>No</span>
                        </span>
                    </span>
                </div>

                <div className={local.row}>
                    <span className={local.number}> 9 </span>
                    <span className={local.numberLabel}>
                        Please explain in detail what actions you have taken (for example a timeline
                        of methods of attempted contact, if additional support provided to the
                        participant)
                    </span>
                    <span className={local.answer}>{surveyAnswers.Q_11}</span>
                </div>

                <div className={local.divider}></div>

                <div className={local.heading}>Part 3 - Compliance Condition</div>

                <div className={local.row}>
                    <span className={local.labelLong}>
                        <span className={local.section}>
                            Provide full details of the compliance condition included within the
                            MAN.
                        </span>
                        <span className={local.section}>
                            This is the activity the participant was told they would have to
                            undertake in order to stop the open-ended element of any sanctions
                            imposed
                        </span>
                    </span>
                    <span className={local.answer}>{surveyAnswers.Q_12}</span>
                </div>
            </div>

            <div className={local.formPage2}>
                <div className={local.divider} />

                <div className={local.heading}>Part 4 - Re-arrangement details (if applicable)</div>

                <div className={local.row}>
                    <span className={local.label}>
                        Did the participant try to re-arrange or change the mandated appointment or
                        activity in advance
                    </span>
                    <span className={local.answerBlock}>
                        <span className={local.answerRow}>
                            <span className={local.emptyCheckBox}>
                                {surveyAnswers.Q_13 === true ? 'X' : ''}
                            </span>
                            <span>Yes</span>
                        </span>
                        <span className={local.answerRow}>
                            <span className={local.emptyCheckBox}>
                                {surveyAnswers.Q_13 === false ? 'X' : ''}
                            </span>
                            <span>No</span>
                        </span>
                    </span>
                </div>

                <div className={local.row}>
                    <span className={local.label}>
                        If <span className={local.bold}>Yes</span>, detail the reason(s) given by
                        the participant, and why you did not allow them to re-arrange the original
                        mandated activity/appointment.
                    </span>
                    <span className={local.answer}>{surveyAnswers.Q_14}</span>
                </div>

                <div className={local.divider} />

                <div className={local.heading}>
                    Part 5 - Non-participation details
                    <span className={local.bracketText}>
                        (only complete this part of the participant failed to undertake a mandatory
                        activity)
                    </span>
                </div>

                <div className={local.row}>
                    <span className={local.label}>
                        How did the participant fail to undertake the mandatory activity?
                    </span>
                    <span className={local.answer}>{surveyAnswers.Q_15}</span>
                </div>

                <div className={local.row}>
                    <span className={local.labelFiveRow}>Provide full details</span>
                    <span className={local.answer}>{surveyAnswers.Q_16}</span>
                </div>

                <div className={local.divider} />

                <div className={local.heading}>
                    Part 6 - If the participant volunteered information as to why they failed to
                    undertake the mandatory appointment/activity please provide details below
                </div>
                <div className={local.row}>
                    <span className={local.answerBox}>{surveyAnswers.Q_17}</span>
                </div>

                <div className={local.divider} />

                <div className={local.heading}> Part 7 - Any other information </div>
                <div className={local.row}>
                    <span className={local.answerBox}>{surveyAnswers.Q_18}</span>
                </div>

                <div className={local.divider}></div>

                <div className={local.heading}> Part 8 - Provider details and declaration </div>

                <div className={local.confirmRow}>
                    <span className={local.bold}>
                        I confirm that the above is a full and accurate statement.{' '}
                    </span>
                    <span className={local.emptyCheckBox}>X</span>
                    <span>(tick to confirm)</span>
                </div>

                <div className={local.row}>
                    <span className={local.labelMed}>Name of adviser or tutor</span>
                    <span className={local.answer}>{surveyAnswers.Q_19}</span>
                </div>

                <div className={local.row}>
                    <span className={local.labelMed}>Name of Prime provider</span>
                    <span className={local.answer}>{surveyAnswers.Q_20}</span>
                </div>

                <div className={local.row}>
                    <span className={local.labelMed}>
                        <span className={local.bold}>Prime Provider</span> Email address
                    </span>
                    <span className={local.answer}>{surveyAnswers.Q_21}</span>
                </div>

                <div className={local.row}>
                    <span className={local.labelAddress}>Prime Provider Address</span>
                    <span className={local.addressAnswer}>
                        {surveyAnswers.Q_22
                            ? surveyAnswers.Q_22.split('\n').map((el) => <span key={el}>{el}</span>)
                            : ''}
                    </span>
                </div>

                <div className={local.row}>
                    <span className={local.labelMed}>Contact Telephone number</span>
                    <span className={local.answer}>{surveyAnswers.Q_23}</span>
                </div>

                <div className={local.row}>
                    <span className={local.labelMed}>Name of sub-contractor, if appropriate.</span>
                    <span className={local.answer}>{surveyAnswers.Q_24}</span>
                </div>

                <div className={local.row}>
                    <span className={local.labelDate}>Date</span>
                    <span className={local.answer}>{reverseFormatDate(surveyAnswers.Q_25)}</span>
                </div>
            </div>
        </div>
    );
};

UCD603FormTemplate.propTypes = {
    surveyAnswers: PropTypes.object.isRequired
};

export default UCD603FormTemplate;
